import {
  getRenterPrivacyCentreRemoteConfigSnapshot,
  getThanksOffersRemoteConfigSnapshot,
  parseBooleanString,
} from 'config/remoteConfig'

import { ACCOUNT_TYPE_PREMIER_APR24 } from 'app/utils/accounts/helpers'
import {
  MIRVAC_TEAM_NAME,
  QLD_AFFORDABLE_TEAM_NAME,
  VARC_TEAM_NAME,
  VARC_TEAM_NAME2,
} from 'app/utils/text/helpers'

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false
  const { hostname } = window.location
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local')
  )
}

const getLocalRuntimeParam = (key) => {
  if (!isLocalDevHost()) return null
  const value = new URLSearchParams(window.location.search).get(key)
  return value === null ? null : value.trim()
}

const toFiniteNumberOrNaN = (value) => {
  if (value === null || value === undefined || value === '') return NaN
  const num = Number(value)
  return Number.isFinite(num) ? num : NaN
}
const resolveThanksOffersEnabled = () => {
  const queryOverride = parseBooleanString(getLocalRuntimeParam('thanksOffers'))
  if (queryOverride !== null) return queryOverride

  const envOverride = parseBooleanString(
    process.env.REACT_APP_THANKS_OFFERS_ENABLED,
  )
  if (envOverride !== null) return envOverride

  const { isOn: remoteIsOn } = getThanksOffersRemoteConfigSnapshot()
  if (typeof remoteIsOn === 'boolean') return remoteIsOn

  return false
}

const resolveRenterPrivacyCentreEnabled = () => {
  const queryOverride = parseBooleanString(
    getLocalRuntimeParam('renterPrivacyCentre'),
  )
  if (queryOverride !== null) return queryOverride

  const envOverride = parseBooleanString(
    process.env.REACT_APP_RENTER_PRIVACY_CENTRE_ENABLED,
  )
  if (envOverride !== null) return envOverride

  const { isOn: remoteIsOn } = getRenterPrivacyCentreRemoteConfigSnapshot()
  return remoteIsOn
}

const resolveThanksSampleRate = () => {
  const thanksOffersSampleRateQuery = toFiniteNumberOrNaN(
    getLocalRuntimeParam('thanksSampleRate'),
  )
  if (Number.isFinite(thanksOffersSampleRateQuery)) {
    return thanksOffersSampleRateQuery
  }

  const thanksOffersSampleRateEnv = toFiniteNumberOrNaN(
    process.env.REACT_APP_THANKS_OFFERS_SAMPLE_RATE,
  )
  if (Number.isFinite(thanksOffersSampleRateEnv)) {
    return thanksOffersSampleRateEnv
  }

  const { sampleRate: remoteSampleRate } = getThanksOffersRemoteConfigSnapshot()
  if (Number.isFinite(remoteSampleRate)) {
    return remoteSampleRate
  }

  return 100
}

export const FeatureFlag = {
  Advertisement: { isOn: false },
  Agency: { isOn: true },
  Viewings: { isOn: true },
  BondCover: { isOn: false },
  Contacts: { isOn: true },
  Activity: { isOn: true },
  Notes: { isOn: true },
  ViewingSchedule: { isOn: true },
  ManualRentalReference: { isOn: true },
  ManualEmploymentVerification: { isOn: true },
  ViewingCommentTemplates: { isOn: true },
  AddEnquirersToViewings: { isOn: true },
  FreeBackgroundCheck: { isOn: false },
  ThanksOffers: {
    get isOn() {
      return resolveThanksOffersEnabled()
    },
    get sampleRate() {
      return resolveThanksSampleRate()
    },
  },
  RenterPrivacyCentre: {
    get isOn() {
      return resolveRenterPrivacyCentreEnabled()
    },
  },
}

export const isNewWorkflowEnabled = () => {
  return true
}

const settingsNotificationsViewings = {}

const settingsNotificationsApplications = {}

const settingsReportsNotificationsDisabled = {}

const teamReferenceCountDisabled = {
  snugtour: true,
}

const maintenanceWindowEnabled = {
  snug: true,
  snugtour: true,
}

const showBallotsEnabledForTeam = {
  snug: true,
  snugtour: true,
  [VARC_TEAM_NAME]: true,
  [VARC_TEAM_NAME2]: true,
  [MIRVAC_TEAM_NAME]: true,
  ballarat: true,
  [QLD_AFFORDABLE_TEAM_NAME]: true,
}

export const copilotEnabledForTeam = {
  snug: true,
  snugtour: true,
  hodgesmentone: true,
  rhnewtown: true,
  jjre: true,
  abercrombys: true,
  clarkrealestate: true,
  spectrumestateagents: true,
  mcgrathriverina: true,
  imageproperty: true,
  RayWhiteHollandPark: true,
  echorealty: true,
  raywhitewoonona: true,
  mcgrathestateagentswilloughby: true,
  elgportmacquarie: true,
  noonanrealestateagency: true,
}

const filterReletsEnabledForTeam = {
  snug: true,
  snugtour: true,
  leahjay: true,
}

const emailBrandingEnabledForTeam = {
  snug: true,
  snugtour: true,
}

const qldForm22Disabled = {
  [QLD_AFFORDABLE_TEAM_NAME]: true,
}

const requireBothContactsForEmergencyContact = {
  snug: true,
  snugtour: true,
  leahjay: true,
}

const requireAddressForEmergencyContact = {
  snug: true,
  snugtour: true,
  leahjay: true,
}

const sendDepositReceivedToOwnerEnabled = {
  snug: true,
  snugtour: true,
  leahjay: true,
}

export const isMaintenanceWindowEnabled = (teamSlug) => {
  return !!maintenanceWindowEnabled[teamSlug]
}

export const isViewingRunEnabled = (teamSlug, teams = []) => {
  const foundTeam = teams.find((team) => team.slug === teamSlug)
  return foundTeam && foundTeam.viewingRunEnabled
}

export const isEnquiryNotificationsSettingEnabled = () => {
  return true
}

export const isViewingNotificationsSettingsEnabled = (teamSlug) => {
  return settingsNotificationsViewings[teamSlug]
}

export const isApplicationNotificationsSettingsEnabled = (teamSlug) => {
  return settingsNotificationsApplications[teamSlug]
}

export const isReportNotificationsSettingsEnabled = (teamSlug) => {
  return !settingsReportsNotificationsDisabled[teamSlug]
}

export const isOfflineActionsEnabled = () => {
  return true
}

export const isApplicantCommunicationLogsEnabled = () => {
  return true
}

export const isKeyLoggerEnabled = (currentTeam) => {
  return currentTeam && currentTeam.keyManagementEnabled
}

export const isAdvancedReportingEnabled = (currentTeam) => {
  return currentTeam && currentTeam.advancedActivityEnabled
}

export const isBackgroundChecksReportingEnabled = (currentTeam) => {
  return (
    currentTeam &&
    currentTeam.backgroundChecksReportingEnabled &&
    currentTeam.enableBackgroundCheckBundle
  )
}

export const isSmsReportingEnabled = () => {
  return true
}

export const isShareApplicationWithOwnerEnabled = () => {
  return true
}

export const isTeamReferenceCountEnabled = (teamSlug) => {
  return !teamReferenceCountDisabled[teamSlug]
}

export const isSetupTabEnabled = () => {
  return true
}

export const isFollowingEnabled = () => {
  return true
}

export const isTravelTimeOptimisationEnabled = (currentTeam) => {
  return currentTeam && currentTeam.travelTimeOptimisationEnabled
}

export const isViewingMergeEnabled = () => {
  return true
}

export const isEnableActivityFeedIconEnabled = () => {
  return true
}

export const isShowBallotsEnabledForTeam = (teamSlug) => {
  return showBallotsEnabledForTeam[teamSlug]
}

export const isFeedEmailFilterEnabled = () => {
  return false
}

export const isPMSortEnabled = (currentTeam) => {
  return true
}

export const isKolmeoEnabled = (teamSlug) => {
  return true
}

export const isManagedAppEnabled = (teamSlug) => {
  return true
}

export const isOwnerOptInForBGCEnabled = (teamSlug) => {
  return true
}

export const isSendApplicantEnabled = (pms, teamSlug) => {
  return true
}

export const isCopilotEnabledForTeam = (teamSlug) => {
  return copilotEnabledForTeam[teamSlug]
}

export const isGroupBundleEnabled = () => {
  return true
}

export const isPodsFilterEnabledForTeam = (teamSlug) => {
  return true
}

export const isEmailBrandingEnabledForTeam = (teamSlug) => {
  return emailBrandingEnabledForTeam[teamSlug]
}

export const isFilterReletsEnabledForTeam = (teamSlug) => {
  return filterReletsEnabledForTeam[teamSlug]
}

export const isEmailReportingEnabled = () => {
  return true
}

export const isQLDForm22Disabled = (teamSlug) => {
  return qldForm22Disabled[teamSlug]
}

export const isRequireBothContactsForEmergencyContact = (teamSlug) => {
  return requireBothContactsForEmergencyContact[teamSlug]
}

export const isSendDepositReceivedToOwnerEnabled = (teamSlug) => {
  return sendDepositReceivedToOwnerEnabled[teamSlug]
}

export const isApplicationRentalLedgerRequiredSettingEnabled = (
  isAccountPremier = false,
) => {
  return Boolean(isAccountPremier)
}

const bankStatementRedactionEligibleAccountTypes = {
  [ACCOUNT_TYPE_PREMIER_APR24]: true,
}

export const isBankStatementRedactionSettingFeatureVisible = () => {
  return process.env.REACT_APP_ENV !== 'production'
}

export const isBankStatementRedactionSettingEnabled = (accountType = 0) => {
  if (!isBankStatementRedactionSettingFeatureVisible()) {
    return false
  }

  return Boolean(
    bankStatementRedactionEligibleAccountTypes[Number(accountType)],
  )
}

export const isFinancialInsightsSettingEnabled = () => {
  return process.env.REACT_APP_FINANCIAL_INSIGHTS_ENABLED === 'true'
}

export const isRequireAddressForEmergencyContact = (teamSlug) => {
  return requireAddressForEmergencyContact[teamSlug]
}

export const isDeletedApplicationsAlertEnabled = () => {
  return false // Disabled by default while deploying backend changes
}

export const isBondFieldEnabled = () => {
  return true
}

export const isSupportAccessEnabled = () => {
  return process.env.REACT_APP_SUPPORT_ACCESS_ENABLED === 'true'
}
